import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'

/**
 * Get holidays for a specific year
 * @param {number} year - Year to fetch holidays for
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getHolidaysByYear = async (year) => {
  try {
    const holidaysQuery = query(
      collection(db, 'holidays'),
      where('year', '==', year),
      orderBy('date', 'asc')
    )

    const querySnapshot = await getDocs(holidaysQuery)
    const holidays = []

    querySnapshot.forEach((doc) => {
      holidays.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: holidays }
  } catch (error) {
    console.error('Get holidays error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Create new holiday
 * @param {object} data - Holiday data
 * @param {string} adminName - Name of admin creating the holiday
 * @returns {Promise<{ success: boolean, docId?: string, data?: object, error?: string }>}
 */
export const createHoliday = async (data, adminName) => {
  try {
    // Validate required fields
    if (!data.name || !data.date || !data.type) {
      return { success: false, error: 'Missing required fields: name, date, or type' }
    }

    // Validate name length
    if (data.name.length > 100) {
      return { success: false, error: 'Holiday name exceeds maximum length of 100 characters' }
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(data.date)) {
      return { success: false, error: 'Invalid date format. Use YYYY-MM-DD' }
    }

    // Validate endDate if provided
    if (data.endDate) {
      if (!dateRegex.test(data.endDate)) {
        return { success: false, error: 'Invalid end date format. Use YYYY-MM-DD' }
      }

      // Check endDate >= date
      if (data.endDate < data.date) {
        return { success: false, error: 'End date must be on or after start date' }
      }
    }

    // Extract year and calculate day of week
    const year = extractYearFromDate(data.date)
    const dayOfWeek = calculateDayOfWeek(data.date)

    const now = Timestamp.now()
    const holidayData = {
      name: data.name.trim(),
      date: data.date,
      type: data.type,
      year: year,
      dayOfWeek: dayOfWeek,
      createdBy: adminName || 'Admin',
      createdAt: now
    }

    // Add endDate if provided
    if (data.endDate) {
      holidayData.endDate = data.endDate
    }

    // Create document
    const docRef = await addDoc(collection(db, 'holidays'), holidayData)

    return {
      success: true,
      docId: docRef.id,
      data: {
        id: docRef.id,
        ...holidayData
      }
    }
  } catch (error) {
    console.error('Create holiday error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update existing holiday
 * @param {string} id - Holiday document ID
 * @param {object} data - Updated holiday data
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateHoliday = async (id, data) => {
  try {
    // Validate name length
    if (data.name && data.name.length > 100) {
      return { success: false, error: 'Holiday name exceeds maximum length of 100 characters' }
    }

    // Validate endDate if provided
    if (data.endDate && data.date) {
      if (data.endDate < data.date) {
        return { success: false, error: 'End date must be on or after start date' }
      }
    }

    // Recalculate year and day if date changed
    const updateData = { ...data }
    if (data.date) {
      updateData.year = extractYearFromDate(data.date)
      updateData.dayOfWeek = calculateDayOfWeek(data.date)
    }

    updateData.updatedAt = Timestamp.now()

    await updateDoc(doc(db, 'holidays', id), updateData)

    return { success: true }
  } catch (error) {
    console.error('Update holiday error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete holiday
 * @param {string} id - Holiday document ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const deleteHoliday = async (id) => {
  try {
    await deleteDoc(doc(db, 'holidays', id))
    return { success: true }
  } catch (error) {
    console.error('Delete holiday error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Calculate day of week from date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} - Day name (e.g., "Monday")
 */
export const calculateDayOfWeek = (dateString) => {
  try {
    const date = new Date(dateString + 'T00:00:00') // Add time to avoid timezone issues
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    return days[date.getDay()]
  } catch (error) {
    console.error('Calculate day of week error:', error)
    return 'Unknown'
  }
}

/**
 * Helper: Extract year from date string
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {number} - Year
 */
export const extractYearFromDate = (dateString) => {
  return parseInt(dateString.split('-')[0], 10)
}

/**
 * Helper: Format holiday date for display (supports ranges)
 * @param {string} dateString - Start date in YYYY-MM-DD format
 * @param {string} endDateString - End date in YYYY-MM-DD format (optional)
 * @param {string} dayOfWeek - Day of week name for start date
 * @returns {string} - Formatted date or date range
 */
export const formatHolidayDate = (dateString, endDateString = null, dayOfWeek) => {
  try {
    const startDate = new Date(dateString + 'T00:00:00')

    // Single day holiday
    if (!endDateString || endDateString === dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      const formattedDate = startDate.toLocaleDateString('en-US', options)
      return `${dayOfWeek}, ${formattedDate}`
    }

    // Multi-day holiday
    const endDate = new Date(endDateString + 'T00:00:00')
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    const startMonth = startDate.getMonth()
    const endMonth = endDate.getMonth()

    // Same month and year - compact format: "Dec 24-26, 2025"
    if (startYear === endYear && startMonth === endMonth) {
      const month = startDate.toLocaleDateString('en-US', { month: 'short' })
      return `${month} ${startDate.getDate()}-${endDate.getDate()}, ${startYear}`
    }

    // Different months or years - full format: "Dec 30, 2024 - Jan 2, 2025"
    const startFormatted = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const endFormatted = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    return `${startFormatted} - ${endFormatted}`
  } catch (error) {
    console.error('Format holiday date error:', error)
    return dateString
  }
}

/**
 * Helper: Check if holiday is upcoming (today or future, checks end date for ranges)
 * @param {string} dateString - Start date in YYYY-MM-DD format
 * @param {string} endDateString - End date in YYYY-MM-DD format (optional)
 * @returns {boolean} - True if upcoming
 */
export const isUpcoming = (dateString, endDateString = null) => {
  try {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // For multi-day holidays, check if today <= end date
    const compareDate = endDateString
      ? new Date(endDateString + 'T00:00:00')
      : new Date(dateString + 'T00:00:00')

    compareDate.setHours(0, 0, 0, 0)

    return compareDate >= today
  } catch (error) {
    console.error('Is upcoming error:', error)
    return false
  }
}

/**
 * Helper: Calculate duration of holiday in days
 * @param {string} dateString - Start date in YYYY-MM-DD format
 * @param {string} endDateString - End date in YYYY-MM-DD format (optional)
 * @returns {number} - Number of days (1 for single day)
 */
export const calculateDuration = (dateString, endDateString = null) => {
  if (!endDateString || endDateString === dateString) {
    return 1 // Single day
  }

  try {
    const start = new Date(dateString + 'T00:00:00')
    const end = new Date(endDateString + 'T00:00:00')
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include both days
    return diffDays
  } catch (error) {
    console.error('Calculate duration error:', error)
    return 1
  }
}

/**
 * Helper: Group holidays by month
 * @param {array} holidays - Array of holiday objects
 * @returns {object} - Object with month names as keys
 */
export const groupHolidaysByMonth = (holidays) => {
  const grouped = {}
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  holidays.forEach((holiday) => {
    try {
      const date = new Date(holiday.date + 'T00:00:00')
      const monthName = monthNames[date.getMonth()]

      if (!grouped[monthName]) {
        grouped[monthName] = []
      }

      grouped[monthName].push(holiday)
    } catch (error) {
      console.error('Group by month error:', error)
    }
  })

  return grouped
}

/**
 * Helper: Get available years for filter
 * @returns {array} - Array of years
 */
export const getAvailableYears = () => {
  const currentYear = new Date().getFullYear()
  return [currentYear - 1, currentYear, currentYear + 1]
}

/**
 * Helper: Get holiday types
 * @returns {array} - Array of holiday types
 */
export const getHolidayTypes = () => {
  return ['National', 'Company', 'Optional']
}

/**
 * Helper: Get badge variant for holiday type
 * @param {string} type - Holiday type
 * @returns {string} - Badge variant
 */
export const getTypeBadgeVariant = (type) => {
  const variants = {
    'National': 'success',
    'Company': 'info',
    'Optional': 'warning'
  }
  return variants[type] || 'info'
}

/**
 * Helper: Format date for date input (YYYY-MM-DD)
 * @param {string} dateString - Date string
 * @returns {string} - Formatted date
 */
export const formatDateForInput = (dateString) => {
  return dateString // Already in YYYY-MM-DD format
}
